import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";

import DialogBox from "../dialog/Dialog";
import {
  accountFeesInfo,
  amountTxt,
  insufficientBalance,
  minSwapAmountRequired,
  sats,
  speedFee as speedFeeTxt,
  swap,
  useMax,
  swapSuccessFull,
  to,
  totalAmount,
  totalBalance,
  maxSwapAmountAllowed,
  faqs,
  limitExhaustedMsg,
  XAUT,
  btc,
} from "../messages";
import { Input } from "../input/Input";
import WalletSymbols from "../WalletSymbols";
import Text from "../text/Text";
import CommonSkeleton from "../CommonSkeleton";
import QrInfoBox from "../QrInfoBox";
import ClockTimer from "../clockTimer/ClockTimer";
import Button from "../button/Button";
import {
  callAPIInterface,
  displayAmountWithCurrency,
  fetchPrecisedInteger,
  formatDisplayValues,
  getCurrencyObj,
  getDisplayCurrencySymbol,
  getCurrentTimeInSecond,
  getSpeedFeeLabel,
  showAmount,
} from "../constants";
import { CustomLink } from "../link/Link";
import { walletIcon } from "../images";
import LimitExhaustedMsgBox from "./LimitExhaustedMsgBox";
import useCouponCodes from "../../hooks/useCouponCodes";
import { useSession } from "../../hooks/useSession";
import ApplyCouponCode from "../ApplyCouponCode";

function SwapCommon({
  isOpenSwapModal,
  setIsOpenSwapModal,
  isFromBrowser,
  selectedAssetForSwap,
  textSize,
  currency,
  currencyIcon,
  showBtcAmount,
  selectedAssetForSwapBalance,
  supportedSwaps,
}) {
  const navigate = useNavigate();
  const { session } = useSession();
  const [amount, setAmount] = useState("");
  const [isBackSpace, setIsBackSpace] = useState(false);
  const [convertedAmount, setConvertedAmount] = useState(0);
  const [quoteLoader, setQuoteLoader] = useState(false);
  const [speedFee, setSpeedFee] = useState(0);
  const [quoteData, setQuoteData] = useState(null);
  const [refreshQuoteData, setRefreshQuoteData] = useState(false);
  const [btnLoader, setBtnLoader] = useState(false);
  const [isInsufficientBalance, setIsInsufficientBalance] = useState(false);
  const [swapLimit, setSwapLimit] = useState({});
  const [error, setError] = useState(null);
  const [couponCodes, setCouponCodes] = useState([]);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [networkFeeLoader, setNetworkFeeLoader] = useState(true);
  const [showFeesSection, setShowFeesSection] = useState(false);

  const queryParam = `limit=${10}`;

  const { uuid: currencyToSwap, icon: currencyToSwapIcon } = {
    ...selectedAssetForSwap,
  };

  const swapOutCurrencyToShow =
    showBtcAmount && currencyToSwap === sats ? "BTC" : currencyToSwap;
  const swapInCurrencyToShow =
    showBtcAmount && currency === sats ? "BTC" : currency;

  const swapMinAmtDisplay =
    showAmount({
      amount: swapLimit?.min_amount_swap_out,
      currency: currencyToSwap,
      targetedCurrency: swapOutCurrencyToShow,
    }) || "0";
  const swapMaxAmtDisplay =
    showAmount({
      amount: swapLimit?.max_amount_swap_out,
      currency: currencyToSwap,
      targetedCurrency: swapOutCurrencyToShow,
    }) || "0";
  const swapMinAmt = +swapMinAmtDisplay;
  const swapMaxAmt = +swapMaxAmtDisplay;

  const currentAssetBalanceAmount = formatDisplayValues({
    amount:
      swapOutCurrencyToShow === sats
        ? Math.floor(selectedAssetForSwapBalance || 0)
        : selectedAssetForSwapBalance || 0,
    currency: currencyToSwap,
    targetedCurrency: swapOutCurrencyToShow,
  });

  const isAvailableBalanceNegative =
    Number(currentAssetBalanceAmount?.withoutComma) < 0;
  const availableBalance = formatDisplayValues({
    amount:
      swapOutCurrencyToShow === sats
        ? Math.abs(Math.floor(selectedAssetForSwapBalance || 0))
        : Math.abs(selectedAssetForSwapBalance || 0),
    currency: currencyToSwap,
    targetedCurrency: swapOutCurrencyToShow,
  });

  const remainingAmountToAdd = formatDisplayValues({
    amount:
      Number(amount) - Number(currentAssetBalanceAmount?.withoutComma || 0),
    currency: currencyToSwap,
    targetedCurrency: swapOutCurrencyToShow,
    multiplier: 1,
  });

  const isValidAmount = useMemo(() => {
    let currentAmount = Number(amount);
    const maxBalance = Number(currentAssetBalanceAmount.withoutComma);

    if (currentAmount > maxBalance) return false;
    return currentAmount >= swapMinAmt && currentAmount <= swapMaxAmt;
  }, [amount]);

  /**
   * used unformatted amount for calculation.(without comma)
   * used formatted amount for display purpose. (with comma)
   */

  //amount calculation
  const amountInfo = useMemo(() => {
    /**
     * quoteData?.target_amount_swap_out: swap from currency
     * quoteData?.target_currency_swap_in: swap to currency
     */

    if (!quoteData)
      return {
        calculatedFees: "0",
        amountYouPay: "0",
        amount: 0,
        amountYouGet: "0",
        totalAmount: "0",
      };

    const {
      target_amount_swap_out,
      target_currency_swap_out,
      target_amount_swap_in,
      target_currency_swap_in,
    } = { ...quoteData };

    const amountYouPay = formatDisplayValues({
      amount: target_amount_swap_out,
      currency: target_currency_swap_out,
    });

    const amountYouGet = formatDisplayValues({
      amount:
        swapInCurrencyToShow === sats
          ? Math.floor(target_amount_swap_in)
          : target_amount_swap_in,
      currency: target_currency_swap_in,
      targetedCurrency: swapInCurrencyToShow,
    });

    const calculatedFees = formatDisplayValues({
      amount:
        target_currency_swap_in === sats ? Math.floor(speedFee) : speedFee,
      currency: target_currency_swap_in,
      targetedCurrency: swapInCurrencyToShow,
    });

    const totalAmountRaw =
      (amountYouGet?.withoutComma ?? 0) - (calculatedFees?.withoutComma ?? 0);
    const isTotalAmountNegative = totalAmountRaw < 0;
    const totalAmount = formatDisplayValues({
      amount: Math.abs(totalAmountRaw),
      currency: target_currency_swap_in,
      targetedCurrency: swapInCurrencyToShow,
      multiplier: 1,
    });
    return {
      calculatedFees: calculatedFees?.withComma ?? "0",
      amountYouPay: amountYouPay?.withComma ?? "0",
      amount: amountYouPay?.withoutComma ?? 0,
      amountYouGet: amountYouGet?.withComma ?? "0",
      totalAmount: totalAmount?.withComma ?? "0",
      totalAmountWithoutComma: totalAmount?.withoutComma ?? "0",
      isTotalAmountNegative,
    };
  }, [amount, speedFee, quoteData]);

  //handle button text and enable/disable
  const buttonInfo = useMemo(() => {
    let isButtonDisable = false;
    let buttonTxt = `Get ${swapInCurrencyToShow}`;
    setIsInsufficientBalance(false);
    let currentAmount = Number(amount);
    const currencySymbol = <>{getCurrencyObj(swapOutCurrencyToShow).symbol}</>;

    const validateLimits = (minAmount, maxAmount) => {
      if (
        currentAmount > +currentAssetBalanceAmount?.withoutComma ||
        +currentAssetBalanceAmount?.withoutComma <= 0
      ) {
        buttonTxt = insufficientBalance;
        setIsInsufficientBalance(true);
        isButtonDisable = true;
      } else if (currentAmount < minAmount) {
        buttonTxt = minSwapAmountRequired(swapMinAmtDisplay, currencySymbol);
        isButtonDisable = true;
        setIsInsufficientBalance(false);
      } else if (currentAmount > maxAmount) {
        buttonTxt = maxSwapAmountAllowed(swapMaxAmtDisplay, currencySymbol);
        isButtonDisable = true;
        setIsInsufficientBalance(false);
      }
    };

    validateLimits(swapMinAmt, swapMaxAmt);
    if (error) {
      isButtonDisable = true;
    }

    return { buttonTxt, isButtonDisable };
  }, [amount, error, swapLimit]);

  const errorInfo = useMemo(() => {
    const errorType = error?.[0]?.param;
    const limitExhausted = ["count", "amount"].includes(errorType);
    const errorMsg = limitExhaustedMsg(
      errorType === "count" ? "Count" : "Amount"
    );

    return { limitExhausted, errorMsg, errorSubMsg: error?.[0]?.message };
  }, [error]);

  useEffect(() => {
    setConvertedAmount(+amount ? amountInfo.amountYouGet : 0);
  }, [amount, amountInfo.amountYouGet]);

  useEffect(() => {
    if (amount) {
      let timeoutId = setTimeout(() => {
        isValidAmount && handleQuoteDetails();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [amount]);

  useEffect(() => {
    refreshQuoteData && isValidAmount && handleQuoteDetails();
  }, [refreshQuoteData]);

  useEffect(() => {
    if (quoteData?.swap_quote_expires_at) {
      const timer = setInterval(() => {
        const currentSeconds = getCurrentTimeInSecond(
          quoteData?.swap_quote_expires_at
        );

        setRefreshQuoteData(currentSeconds <= 0);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [quoteData?.swap_quote_expires_at]);

  useEffect(() => {
    const swapLimit = supportedSwaps.filter(
      (swap) => swap.target_currency_swap_out === currencyToSwap
    );
    setSwapLimit(swapLimit[0]);
  }, []);

  const handleQuoteDetails = async () => {
    try {
      const body = {
        target_currency_swap_out: currencyToSwap, // FROM
        target_currency_swap_in: currency, // TO
        currency: swapOutCurrencyToShow,
        amount,
      };
      setQuoteLoader(true);
      const response = await callAPIInterface(
        "POST",
        "/balances/swap/quote",
        body
      );
      setQuoteData(response);
      setSpeedFee(response?.speed_fee?.total_fee);
      setError(null);
    } catch (error) {
      setError(error.response.data.errors);
    } finally {
      setQuoteLoader(false);
    }
  };

  const renderModalHeader = () => {
    return (
      <Box display="flex" justifyContent="space-between">
        <Text className="default-text" font="bold">
          {swap}
        </Text>
        <CustomLink
          href={"https://help.speed.app/help/swap"}
          target="_blank"
          sx={{ color: "#2A67FF" }}
          bold
        >
          {faqs}
        </CustomLink>
      </Box>
    );
  };

  const handleModalClose = () => {
    setIsOpenSwapModal(false);
  };

  const handleAmountChange = (e) => {
    const mainAmount = e.target.value;
    const amount =
      currencyToSwap === sats
        ? mainAmount?.slice(0, 10)
        : mainAmount?.slice(0, 8);
    setAmount(amount);
  };

  const displayCurrencyWithIcon = ({ currency, icon }) => {
    return (
      <Text
        className="default-texr"
        font="regular"
        size={textSize}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={icon}
          alt="iocn"
          height="20px"
          width="20px"
          style={{ marginRight: "5px" }}
        />
        {showBtcAmount && currency === sats ? "BTC" : currency}
      </Text>
    );
  };

  const commonInputBoxComponent = ({ currency, icon }) => {
    return (
      <>
        {displayCurrencyWithIcon({
          currency,
          icon,
        })}
        <Input
          fullWidth
          placeholder={"0"}
          customClass={"swap-amount-input"}
          name="amount"
          value={amount}
          type="number"
          onWheel={(e) => e.target.blur()}
          onKeyDown={(evt) => {
            let excludedKey = ["e", "E", "+", "-"];

            setIsBackSpace(evt.keyCode === 8); // Allow backspace

            if (currency === sats && !showBtcAmount) excludedKey.push(".");

            if (excludedKey.includes(evt.key)) evt.preventDefault();
          }}
          onChange={(e) => {
            const isXAUT = currencyToSwap === XAUT;
            const decimalPlaces = isXAUT ? 6 : 2;
            const pattern = new RegExp(`^\\d+(\\.\\d{0,${decimalPlaces}})?$`);
            let val = e.target.value;
            if (
              val &&
              !pattern.test(val) &&
              !isBackSpace &&
              currency !== sats
            ) {
              e.preventDefault();
            } else {
              handleAmountChange(e);
            }
          }}
          inputProps={{
            min: 0,
            style: {
              fontSize: isFromBrowser ? 44 : 34,
              height: "35px",
              padding: isFromBrowser ? "10px 12px" : "5px 12px",
            },
            maxLength: 8,
          }}
        />
      </>
    );
  };

  const renderExchangeRateText = () => {
    const {
      exchange_rate_swap_in,
      target_currency_swap_in,
      swap_quote_expires_at,
    } = { ...quoteData };

    const renderText = () => {
      const swapInExRate = +showAmount({
        amount: exchange_rate_swap_in || 0,
        currency: target_currency_swap_in,
        targetedCurrency: swapInCurrencyToShow,
      });

      const displayRate = showAmount({
        amount:
          swapInCurrencyToShow === sats
            ? Math.floor(swapInExRate)
            : swapInExRate,
        showCommaSeparated: currency === sats,
        currency,
        targetedCurrency: swapInCurrencyToShow,
        appendCurrency: true,
        multiplier: 1,
        showMoreDigitIfZero: true,
      });

      const swapOutCurrency = displayAmountWithCurrency(
        swapOutCurrencyToShow,
        1
      );

      const clockResetTimeInSec = getCurrentTimeInSecond(swap_quote_expires_at);

      const textElement = (
        <Text>
          {swapOutCurrency} ≈ {displayRate}
        </Text>
      );

      return (
        <>
          <ClockTimer
            expiresAt={swap_quote_expires_at}
            strokeWidth={15}
            clockResetTimeInSec={clockResetTimeInSec}
            counterClockwise={false}
            height="16px"
            width="16px"
            timerColor="#fff"
            previewColor="#2A67FF"
          />
          {textElement}
        </>
      );
    };

    return (
      <Text
        className="grey-text"
        font="regular"
        size={14}
        variant="body1"
        sx={{
          gap: "5px",
          marginTop: "10px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {quoteLoader ? (
          <CommonSkeleton width={200} height={21} />
        ) : (
          renderText()
        )}
      </Text>
    );
  };

  const displayValue = (amount, showSign = false) => {
    return (
      <Text
        className="default-text"
        font="semibold"
        size={textSize}
        variant="subtitle1"
      >
        {showSign} {displayAmountWithCurrency(swapInCurrencyToShow, amount)}
      </Text>
    );
  };

  const handleSetMaxAmount = () => {
    let amount = 0;
    amount =
      currentAssetBalanceAmount?.withoutComma > swapMaxAmt
        ? swapMaxAmt
        : currentAssetBalanceAmount?.withoutComma;
    const maxAmount = showAmount({
      amount: amount,
      currency: currencyToSwap,
      targetedCurrency: swapOutCurrencyToShow,
      multiplier: 1,
    });
    setAmount(
      fetchPrecisedInteger(
        maxAmount,
        currencyToSwap === sats ? (showBtcAmount ? 8 : 0) : 2
      )
    );
  };

  const amountInfoArr = [
    {
      label: amountTxt,
      value: (
        <Box display="flex">
          {quoteLoader ? (
            <CommonSkeleton />
          ) : (
            displayValue(amountInfo?.amountYouGet)
          )}
        </Box>
      ),
    },
    {
      label: getSpeedFeeLabel({
        speedFeePercentage: quoteData?.speed_fee?.percentage,
        speedFixedFees: quoteData?.speed_fee?.fixed_amount,
      }),
      value: (
        <>
          {quoteLoader ? (
            <CommonSkeleton />
          ) : (
            displayValue(amountInfo?.calculatedFees, "-")
          )}
        </>
      ),
      showInfo: true,
      infoMsg: accountFeesInfo,
    },
    {
      label: totalAmount,
      value: (
        <>
          {quoteLoader ? (
            <CommonSkeleton />
          ) : (
            displayValue(
              amountInfo?.totalAmount,
              amountInfo?.isTotalAmountNegative ? "-" : ""
            )
          )}
        </>
      ),
    },
  ];

  const handleSwap = async () => {
    try {
      setBtnLoader(true);
      const swapResponse = await callAPIInterface(
        "POST",
        "/balances/swap",
        JSON.stringify({
          swap_quote_id: quoteData.swap_quote_id,
          promo_code: appliedCoupon?.name,
        })
      );
      navigate("/success", {
        state: {
          completionHeading: swapSuccessFull,
          amount: amountInfo?.amountYouGet,
          currency: swapInCurrencyToShow,
          subMessage: swapResponse?.id,
        },
      });
      setIsOpenSwapModal(false);
    } catch (err) {
      setIsOpenSwapModal(false);
    } finally {
      setBtnLoader(false);
    }
  };

  const showCouponCodes = isValidAmount;
  const { validateCoupon } = useCouponCodes({
    amount,
    swapInAsset: currency,
    currency: currencyToSwap,
    session,
    showFeesSection,
    queryParam,
    appliedCoupon,
    module: "SWAP",
    showCouponCodes,
    setCouponCodes,
    setAppliedCoupon,
    setButtonLoader,
    setNetworkFeeLoader,
  });

  const applyCouponCodeProps = {
    couponCodes,
    appliedCoupon,
    setAppliedCoupon,
    isFromBrowser,
    validateCoupon,
    isLoading: buttonLoader,
  };

  return (
    <DialogBox
      isOpenModal={isOpenSwapModal}
      setIsOpenModal={setIsOpenSwapModal}
      modalHeader={renderModalHeader()}
      directionToOpen={isFromBrowser ? "up" : "left"}
      handleAction={() => handleModalClose()}
      isShowCloseIcon={isFromBrowser}
    >
      <Box className={"swap-main-container"}>
        <Box className="container">
          <Box className={"swap-inner-container"}>
            {errorInfo.limitExhausted && (
              <LimitExhaustedMsgBox
                isFromBrowser={isFromBrowser}
                message={errorInfo.errorMsg}
                subMessage={errorInfo.errorSubMsg}
                module={swap}
              />
            )}
            <Box className="swap-box">
              {commonInputBoxComponent({
                currency: currencyToSwap,
                icon: currencyToSwapIcon,
              })}
              <Box display="flex" justifyContent="space-between">
                <Box display="flex" flexDirection="column">
                  <Text
                    size={textSize}
                    font="semibold"
                    className="default-text"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <img
                      src={walletIcon}
                      height={20}
                      width={20}
                      alt="wallet icon"
                    />

                    <Text className="grey-text">Available:</Text>

                    <Text className="default-text" font="semibold">
                      {isAvailableBalanceNegative ? "-" : ""}
                      {getDisplayCurrencySymbol(swapOutCurrencyToShow, showBtcAmount)}
                      {availableBalance?.withComma || "0"}
                    </Text>
                  </Text>
                  <Text
                    size={textSize}
                    font="semibold"
                    className="default-text"
                    sx={{
                      color:
                        currentAssetBalanceAmount?.withoutComma === 0 ||
                        isInsufficientBalance
                          ? "#FD371E !important"
                          : "",
                    }}
                  >
                    {isInsufficientBalance &&
                      remainingAmountToAdd?.withoutComma > 0 && (
                        <>
                          {insufficientBalance}. Add{" "}
                          {getDisplayCurrencySymbol(swapOutCurrencyToShow, showBtcAmount)}
                          {remainingAmountToAdd?.withComma} more to continue
                        </>
                      )}
                  </Text>
                </Box>
                {+currentAssetBalanceAmount?.withoutComma > 0 && (
                  <Box sx={{ cursor: "pointer" }} onClick={handleSetMaxAmount}>
                    <Text
                      size={textSize}
                      font="semibold"
                      className="blue-text txt-uppercase"
                    >
                      {useMax}
                    </Text>
                  </Box>
                )}
              </Box>
            </Box>

            {isValidAmount && !error && (
              <>
                <Text
                  className="grey-text txt-uppercase"
                  font="regular"
                  size={textSize}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    margin: "20px",
                  }}
                >
                  <WalletSymbols symbol="arrow_downward" />
                  {to}
                </Text>
                <Box className="swap-box">
                  {displayCurrencyWithIcon({
                    currency,
                    icon: currencyIcon,
                  })}
                  <Text
                    className="default-text"
                    font="bold"
                    size={isFromBrowser ? "44" : "34"}
                    sx={{ textAlign: "center" }}
                  >
                    {convertedAmount}
                  </Text>
                  {isValidAmount && (
                    <Box
                      borderTop="1px solid #F6F3F3"
                      padding="16px"
                      display="flex"
                      justifyContent="center"
                    >
                      <Text
                        className="default-text"
                        font="regular"
                        size={textSize}
                      >
                        {renderExchangeRateText()}
                      </Text>
                    </Box>
                  )}
                </Box>
                {showCouponCodes && (
                  <ApplyCouponCode {...applyCouponCodeProps} />
                )}
                {quoteData && (
                  <Box
                    className="swap-box"
                    marginTop={isFromBrowser ? "20px" : "10px"}
                  >
                    <QrInfoBox
                      infoData={amountInfoArr}
                      isFromBrowser={isFromBrowser}
                      currency={currency}
                      isFrom={swap}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
          <Box className={"swap-button"}>
            <Button
              label={buttonInfo?.buttonTxt}
              variant="contained"
              fullWidth
              disabled={buttonInfo?.isButtonDisable || quoteLoader || btnLoader}
              onClick={handleSwap}
              loading={btnLoader}
            />
          </Box>
        </Box>
      </Box>
    </DialogBox>
  );
}

export default SwapCommon;
